import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import apiService from '@/api-service';

interface ExportColumn {
    key: string;
    label: string;
    category: string;
}

interface HoofdcodeExportParams {
    description?: string;
    werk_categorie_ids?: number[];
    subwerk_categorie_ids?: number[];
    include_usage?: boolean;
    hoofdcode_ids?: number[];
    include_filtered_99_codes?: boolean;
    filter_99_werk_codes?: string[];
    filter_99_subwerk_codes?: string[];
    sort_by?: string;
    sort_direction?: string;
}

interface Props {
    show: boolean;
    onHide: () => void;
    filterParams: HoofdcodeExportParams;
    totalCount: number;
}

const CSV_EXPORT_COLUMNS: ExportColumn[] = [
    { key: 'label', label: 'Label', category: 'Hoofdcode' },
    { key: 'versie_jaarversie', label: 'Jaarversie', category: 'Hoofdcode' },
    { key: 'volgnummer', label: 'Volgnummer', category: 'Hoofdcode' },
    { key: 'romptekst', label: 'Romptekst', category: 'Hoofdcode' },
    { key: 'hoofdtekst', label: 'Hoofdtekst', category: 'Hoofdcode' },
    { key: 'overige_tekst', label: 'Overige tekst', category: 'Hoofdcode' },
    { key: 'aanwijzings_tekst', label: 'Aanwijzingstekst', category: 'Hoofdcode' },
    { key: 'eenheid', label: 'Eenheid', category: 'Hoofdcode' },
    { key: 'deprecated', label: 'Vervallen', category: 'Hoofdcode' },
    { key: 'werk_categorie_code', label: 'Werkcategorie Code', category: 'Werkcategorie' },
    { key: 'werk_categorie_omschrijving', label: 'Werkcategorie Omschrijving', category: 'Werkcategorie' },
    { key: 'subwerk_categorie_code', label: 'Subwerkcategorie Code', category: 'Subwerkcategorie' },
    { key: 'subwerk_categorie_omschrijving', label: 'Subwerkcategorie Omschrijving', category: 'Subwerkcategorie' },
    { key: 'usage_vermeldingen', label: 'Aantal Vermeldingen', category: 'Gebruik' },
    { key: 'usage_bestekken', label: 'Aantal Bestekken', category: 'Gebruik' },
    { key: 'preceeded_by_label', label: 'Opvolger van (Label)', category: 'Relaties' },
    { key: 'preceeded_by_jaarversie', label: 'Opvolger van (Jaarversie)', category: 'Relaties' },
    { key: 'succeeded_by_id', label: 'Opgevolgd door (ID)', category: 'Relaties' },
    { key: 'deficodes_count', label: 'Aantal Deficodes', category: 'Deficodes' },
    { key: 'deficodes_labels', label: 'Deficode Labels', category: 'Deficodes' },
];

export default function HoofdcodeCSVExportModal({ show, onHide, filterParams, totalCount }: Props) {
    const [selectedColumns, setSelectedColumns] = useState<ExportColumn[]>([
        CSV_EXPORT_COLUMNS.find(c => c.key === 'label')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'versie_jaarversie')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'romptekst')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'werk_categorie_omschrijving')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'usage_vermeldingen')!,
        CSV_EXPORT_COLUMNS.find(c => c.key === 'usage_bestekken')!,
    ]);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx');
    const [maxRows, setMaxRows] = useState<number>(Math.min(1000, totalCount));

    const groupedColumns = CSV_EXPORT_COLUMNS.reduce((groups, column) => {
        if (!groups[column.category]) {
            groups[column.category] = [];
        }
        groups[column.category].push(column);
        return groups;
    }, {} as { [category: string]: ExportColumn[] });

    const handleExport = async () => {
        if (selectedColumns.length === 0) {
            alert('Selecteer minimaal één kolom om te exporteren.');
            return;
        }

        setIsExporting(true);

        try {
            const exportParams = {
                search_params: {
                    ...filterParams,
                    include_usage: true,
                },
                format: exportFormat,
                export_columns: selectedColumns.map(col => col.key),
                max_rows: maxRows
            };

            const response = await apiService.post('/hoofdcode/export', exportParams, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], {
                type: exportFormat === 'csv'
                    ? 'text/csv'
                    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `hoofdcodes_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            onHide();
        } catch (error) {
            console.error('Export failed:', error);
            alert('Export mislukt. Probeer het opnieuw.');
        } finally {
            setIsExporting(false);
        }
    };

    const selectAllInCategory = (category: string) => {
        const categoryColumns = groupedColumns[category];
        const newSelected = [...selectedColumns];
        categoryColumns.forEach(col => {
            if (!newSelected.find(selected => selected.key === col.key)) {
                newSelected.push(col);
            }
        });
        setSelectedColumns(newSelected);
    };

    const deselectAllInCategory = (category: string) => {
        const categoryColumns = groupedColumns[category];
        const newSelected = selectedColumns.filter(col =>
            !categoryColumns.find(catCol => catCol.key === col.key)
        );
        setSelectedColumns(newSelected);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Hoofdcodes Exporteren naar {exportFormat.toUpperCase()}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mb-3">
                    <strong>Export instellingen:</strong>
                    <div className="mt-2">
                        <Form.Check
                            type="radio"
                            id="format-csv"
                            label="CSV formaat"
                            checked={exportFormat === 'csv'}
                            onChange={() => setExportFormat('csv')}
                        />
                        <Form.Check
                            type="radio"
                            id="format-excel"
                            label="Excel formaat"
                            checked={exportFormat === 'xlsx'}
                            onChange={() => setExportFormat('xlsx')}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <Form.Label>Maximum aantal rijen (max {totalCount}):</Form.Label>
                    <Form.Control
                        type="number"
                        value={maxRows}
                        onChange={(e) => setMaxRows(Math.min(parseInt(e.target.value) || 1000, totalCount))}
                        min={1}
                        max={totalCount}
                    />
                </div>

                <div className="mb-3">
                    <Form.Label>Geselecteerde kolommen ({selectedColumns.length}):</Form.Label>
                    <Select
                        isMulti
                        value={selectedColumns}
                        onChange={(newValue) => setSelectedColumns([...newValue])}
                        options={CSV_EXPORT_COLUMNS}
                        getOptionLabel={(option) => `${option.label} (${option.category})`}
                        getOptionValue={(option) => option.key}
                        placeholder="Selecteer kolommen..."
                        className="mb-2"
                    />
                </div>

                <div>
                    <strong>Kolommen per categorie:</strong>
                    {Object.entries(groupedColumns).map(([category, columns]) => (
                        <div key={category} className="mt-2 p-2 border rounded">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>{category}</strong>
                                <div>
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        className="me-1"
                                        onClick={() => selectAllInCategory(category)}
                                    >
                                        Alles selecteren
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => deselectAllInCategory(category)}
                                    >
                                        Alles deselecteren
                                    </Button>
                                </div>
                            </div>

                            <div className="row">
                                {columns.map(column => (
                                    <div key={column.key} className="col-md-6">
                                        <Form.Check
                                            type="checkbox"
                                            id={`col-${column.key}`}
                                            label={column.label}
                                            checked={selectedColumns.some(sel => sel.key === column.key)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedColumns([...selectedColumns, column]);
                                                } else {
                                                    setSelectedColumns(selectedColumns.filter(sel => sel.key !== column.key));
                                                }
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isExporting}>
                    Annuleren
                </Button>
                <Button
                    variant="primary"
                    onClick={handleExport}
                    disabled={isExporting || selectedColumns.length === 0}
                >
                    {isExporting ? (
                        <>
                            <Spinner size="sm" className="me-1" />
                            Exporteren...
                        </>
                    ) : (
                        `Exporteer ${selectedColumns.length} kolommen`
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}